import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { setNavigate } from "./services/api";

import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";

import HomePage from "./components/home/HomePage";
import ServicesPage from "./components/services/ServicesPage";
import BookingFlow from "./components/booking/BookingFlow";
import BookingDetail from "./components/booking/BookingDetail";
import CustomerDashboard from "./components/dashboard/CustomerDashboard";
import TechnicianDashboard from "./components/dashboard/TechnicianDashboard";
import AdminDashboard from "./components/dashboard/AdminDashboard";
import { LoginPage, RegisterPage, ForgotPasswordPage } from "./components/auth/AuthPages";

import "./index.css";

// Inject React Router navigate into API client for soft 401 redirects
const NavigateInjector = () => {
  const nav = useNavigate();
  useEffect(() => {
    setNavigate(nav);
  }, [nav]);
  return null;
};

// ── Route guards ─────────────────────────────────────────
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="skeleton"
          style={{ width: 48, height: 48, borderRadius: "50%" }}
        />
      </div>
    );

  if (!isAuthenticated)
    return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const GuestRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return null;
  if (isAuthenticated) {
    const dest =
      user.role === "admin"
        ? "/admin"
        : user.role === "technician"
          ? "/technician/dashboard"
          : "/dashboard";
    return <Navigate to={dest} replace />;
  }
  return children;
};

// ── Layout ────────────────────────────────────────────────
const Layout = ({ children, noFooter = false }) => (
  <>
    <Navbar />
    <main>{children}</main>
    {!noFooter && <Footer />}
  </>
);

// ── Routes ────────────────────────────────────────────────
const AppRoutes = () => (
  <>
    <NavigateInjector />
    <Routes>
      {/* Public */}
      <Route path="/" element={<Navigate to="/kolkata" replace />} />
      <Route
        path="/kolkata"
        element={
          <Layout>
            <HomePage />
          </Layout>
        }
      />
      <Route
        path="/services"
        element={
          <Layout>
            <ServicesPage />
          </Layout>
        }
      />
      <Route
        path="/services/:slug"
        element={
          <Layout noFooter>
            <BookingFlow />
          </Layout>
        }
      />

      {/* Auth */}
      <Route
        path="/login"
        element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        }
      />
      <Route
        path="/register"
        element={
          <GuestRoute>
            <RegisterPage />
          </GuestRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <GuestRoute>
            <ForgotPasswordPage />
          </GuestRoute>
        }
      />

      {/* Customer */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute roles={["customer"]}>
            <Layout>
              <CustomerDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/bookings"
        element={
          <ProtectedRoute roles={["customer"]}>
            <Layout>
              <CustomerDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Technician */}
      <Route
        path="/technician/dashboard"
        element={
          <ProtectedRoute roles={["technician"]}>
            <Layout>
              <TechnicianDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={["admin"]}>
            <Layout>
              <AdminDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Any authenticated */}
      <Route
        path="/booking/:id"
        element={
          <ProtectedRoute>
            <Layout noFooter>
              <BookingDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </>
);

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <AppRoutes />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            fontFamily: "'Poppins', sans-serif",
            fontSize: "0.875rem",
            borderRadius: "12px",
            border: "1px solid var(--brand-border)",
            boxShadow: "0 8px 24px rgba(15,13,10,0.12)",
          },
          success: { iconTheme: { primary: "#1A9C5B", secondary: "#fff" } },
          error: { iconTheme: { primary: "#DC2626", secondary: "#fff" } },
        }}
      />
    </AuthProvider>
  </BrowserRouter>
);

export default App;
